import { useEffect, useRef } from 'react';
import type { RoomState } from '@tv-casino/shared';
import { play, setAmbience, startRoll, stopRoll } from './sound.js';
import { ambienceLevel, cueSnapshot, tableCues, type CueSnapshot } from '@tv-casino/viewmodels/lib/soundCues.js';

/**
 * The table's soundtrack, driven entirely off published state.
 *
 * It began on the TV and now runs on the phones too: a player may have
 * headphones in or a Bluetooth speaker paired, and each device decides for
 * itself how loud it is (`sound.ts` holds that, per device). Every hook here
 * reads broadcasts only, so two devices watching the same room hear the same
 * beats without either of them being told to play anything.
 */

/** How long after the ball lands the money is heard moving. */
export const PAYOUT_CUE_MS = 380;

/**
 * The craps soundtrack: the dice on the felt every time a roll lands, and
 * the money moving once the hand settles.
 */
export function useCrapsCues(state: RoomState | null): void {
  const isCraps = state?.gameType === 'craps';
  const rollNumber = isCraps ? state.craps?.rollNumber ?? 0 : 0;
  const phase = isCraps ? state.phase : null;
  const results = isCraps ? state.craps?.results ?? null : null;
  const wasRoll = useRef(0);
  const wasPhase = useRef<string | null>(null);

  useEffect(() => {
    if (rollNumber > wasRoll.current) play('dice');
    wasRoll.current = rollNumber;
  }, [rollNumber]);

  useEffect(() => {
    if (phase === wasPhase.current) return;
    const from = wasPhase.current;
    wasPhase.current = phase;
    if (phase !== 'payout' || from !== 'rolling') return;
    const paid = (results ?? []).some((r) => r.payout > r.stake);
    const timer = window.setTimeout(() => play(paid ? 'payout' : 'sweep'), PAYOUT_CUE_MS);
    return () => window.clearTimeout(timer);
  }, [phase, results]);
}

/**
 * The roulette soundtrack: a click per chip that lands, the ball in the track
 * for exactly the dealer beat, the clack when it drops, and the money moving
 * after it.
 */
export function useRouletteCues(state: RoomState | null): void {
  const phase = state?.gameType === 'roulette' ? state.phase : null;
  const endsAt = state?.deadline?.endsAt ?? null;
  const staked = (state?.roulette?.seats ?? []).reduce(
    (sum, seat) => sum + seat.bets.reduce((n, b) => n + b.amount, 0),
    0,
  );
  const results = state?.roulette?.results ?? null;
  const wasPhase = useRef<string | null>(null);
  const wasStaked = useRef(0);

  useEffect(() => {
    // A stack landing on the layout, but never the reset back to zero.
    if (phase === 'betting' && staked > wasStaked.current) play('chip');
    wasStaked.current = phase === 'betting' ? staked : 0;
  }, [phase, staked]);

  useEffect(() => {
    if (phase === wasPhase.current) return;
    const from = wasPhase.current;
    wasPhase.current = phase;

    if (phase === 'dealer') {
      // The roll runs out on the server's own deadline, so the noise stops
      // at the instant the ball is published as landed.
      startRoll(Math.max(600, Math.min(6_000, (endsAt ?? Date.now() + 4_500) - Date.now())));
      return;
    }
    stopRoll();
    if (phase === 'payout' && from === 'dealer') {
      play('ballDrop');
      const paid = (results ?? []).some((r) => r.payout > 0);
      const timer = window.setTimeout(() => play(paid ? 'payout' : 'sweep'), PAYOUT_CUE_MS);
      return () => window.clearTimeout(timer);
    }
    return;
  }, [phase, endsAt, results]);

  // Leaving the table mid-spin must not leave the ball rolling.
  useEffect(() => stopRoll, []);
}

/**
 * The card tables' soundtrack — blackjack, Hold'em, baccarat, three-card —
 * plus the chip click on craps: `tableCues` compares each broadcast with the
 * last. The money is heard a beat after the reveal, like the other tables, and
 * that beat survives the next broadcast rather than being cancelled by it.
 */
export function useTableCues(state: RoomState | null): void {
  const was = useRef<CueSnapshot | null>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const next = cueSnapshot(state);
    const cues = tableCues(was.current, next);
    was.current = next;
    for (const cue of cues) {
      if (cue === 'payout' || cue === 'sweep') {
        timers.current.push(window.setTimeout(() => play(cue), PAYOUT_CUE_MS));
      } else {
        play(cue);
      }
    }
  }, [state]);

  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);
}

/**
 * The casino floor behind the table, following `ambienceLevel`. How loud it
 * actually sits is the device's own business — the TV takes the host's
 * sliders, a phone takes the ones on its own audio control.
 */
export function useTableAmbience(state: RoomState | null): void {
  const level = ambienceLevel(state);
  useEffect(() => setAmbience(level), [level]);
  useEffect(() => () => setAmbience(null), []);
}
